import { UserRoundPlus } from 'lucide-react'
import { AssigneeSelect } from '@/components/procedure/AssigneeSelect'
import { assigneeInitials } from '@/lib/assignedTo'
import { EXPIRED_BADGE, shortDueDate } from '@/lib/board/boardCardView'
import type { BoardTask } from '@/lib/board/boardTasks'
import { STATUS_META } from '@/lib/journey'
import { PROCEDURE_TYPE_COLOR } from '@/lib/procedureTypeColors'
import { userDisplayName } from '@/lib/userDisplayName'
import { cn } from '@/lib/utils'
import { eventMeta } from '@/types/maintenanceTask'
import { useEventTypeCatalog } from '@/hooks/useEventTypes'
import type { ProcedureAssignee } from '@/types/cgt'

/** The badge of each status a cleaning task can have on the board. */
function badgeOf(status: BoardTask['status']): { label: string; dot: string; badge: string } {
  if (status === 'expired') return EXPIRED_BADGE
  if (status === 'complete') return STATUS_META.completed
  if (status === 'in_progress') return STATUS_META.in_progress
  return STATUS_META.not_started
}

/**
 * A cleaning task on the Overall Status board (z8nrz7f9u5): one with no
 * procedure run yet, which is how a missed clean usually looks. Shaped like a
 * run's card — the room in place of the procedure, the cleaning it is due —
 * and assignable from the card, so the supervisor can hand out the
 * Not assigned list without leaving the board.
 */
export function CleaningTaskBoardCard({
  task,
  users,
  canAssign,
  meId,
  assigning,
  onAssign,
  onOpen,
}: {
  task: BoardTask
  users: ProcedureAssignee[]
  canAssign: boolean
  meId?: string | null
  assigning: boolean
  onAssign: (assigneeId: string | null) => void
  onOpen: () => void
}) {
  const { event } = task
  // The tenant's name for the type, never its code (z8nrz7fb9k).
  const catalog = useEventTypeCatalog()
  const color = PROCEDURE_TYPE_COLOR.Cleaning
  const badge = badgeOf(task.status)
  const room = event.equipment?.label ?? 'Room'
  const assigneeName = event.assigneeId ? userDisplayName(event.assignee, 'Assigned') : null
  const assignee = event.assigneeId ? { id: event.assigneeId, name: assigneeName ?? 'Assigned' } : null
  const assignable = canAssign && task.status !== 'complete'

  return (
    <article className='flex min-w-0 flex-col overflow-hidden rounded-lg border bg-background shadow-sm' data-testid='board-cleaning-task'>
      <header className='border-t-4 px-3 py-2' style={{ borderTopColor: color, backgroundColor: `${color}1f` }}>
        <div className='flex items-center gap-2'>
          <button
            type='button'
            onClick={onOpen}
            title={`${room} — open the Cleaning tasks`}
            className='min-w-0 flex-1 truncate text-left text-sm font-semibold text-foreground hover:underline'
          >
            {room}
          </button>
          <span className='shrink-0 text-xs font-medium text-foreground' title='Due date'>
            {shortDueDate(`${task.dueDate}T00:00:00`)}
          </span>
        </div>
        <div className='mt-0.5 flex items-center gap-2'>
          <span className='flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground'>
            <span className='h-2 w-2 rounded-full' style={{ backgroundColor: color }} />
            Cleaning task
          </span>
        </div>
      </header>

      <div className='flex flex-1 flex-col gap-2.5 px-3 py-3'>
        <div className='flex items-start gap-2'>
          {assignable ? (
            <AssigneeSelect
              variant='avatar'
              users={users}
              value={assignee}
              meId={meId}
              disabled={assigning}
              onChange={id => {
                if (id !== (event.assigneeId ?? null)) onAssign(id)
              }}
            />
          ) : (
            <span
              className={cn(
                'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold',
                assignee ? 'bg-blue-600 text-white' : 'border border-dashed border-slate-300 text-slate-400',
              )}
            >
              {assignee ? assigneeInitials(assignee.name) : <UserRoundPlus className='h-3.5 w-3.5' />}
            </span>
          )}
          <div className='min-w-0 flex-1'>
            <div className={cn('truncate text-xs', assignee ? 'font-medium' : 'text-muted-foreground')}>
              {assigneeName ?? 'Unassigned'}
            </div>
            {event.equipmentType?.name && (
              <div className='mt-0.5 truncate text-xs text-muted-foreground' title={event.equipmentType.name}>
                {event.equipmentType.name}
              </div>
            )}
          </div>
        </div>

        <div className='truncate text-xs'>
          <span className='text-muted-foreground'>Event: </span>
          <span className='font-medium text-foreground'>{eventMeta(event.eventTypeCode, catalog).label}</span>
        </div>

        <div className='flex items-center justify-between gap-2'>
          <span
            className={cn(
              'inline-flex min-w-0 items-center gap-1.5 truncate rounded-full border px-2 py-0.5 text-xs font-medium',
              badge.badge,
            )}
          >
            <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', badge.dot)} />
            {badge.label}
          </span>
        </div>
      </div>
    </article>
  )
}
